import {Injectable} from '@angular/core';
import {UserService} from 'app/core/_services/user.service';
import {Observable} from 'rxjs/Observable';
import {JobService} from 'app/core/_services/job.service';
import {UtilitiesService} from '../../core/_services/utilities.service';
import {Subscription} from 'rxjs';
import {map} from 'rxjs/operators';
import {EventTrackingService} from "../../core/_services/event-tracking.service";
import {Service} from 'app/shared/_models/service';
import {Part} from 'app/shared/_models/part';
import {Router} from "@angular/router";

@Injectable({
    providedIn: 'root'
})
export class BookingFlowPricingService {

    userData$: Subscription;
    userData: any;

    constructor(private userService: UserService,
                private jobService: JobService,
                private eventTrackingService: EventTrackingService,
                protected router: Router) {
        this.userData$ = this.userService.getUserData().subscribe((result: any) => {
            this.userData = result;
        });
    } // constructor()

    getPrice(jobRequest: any, promoCodes?: string[], payerId?: number): Observable<any> {
        const params: any = {
            userId: this.userData.id ? this.userData.id : null,
            vehicleId: jobRequest.vehicle.id ? jobRequest.vehicle.id : null,
            zip: this.userData.zip ? this.userData.zip : null,
            promoCodes: promoCodes,
            locationId: (jobRequest.location && jobRequest.location.id) ? jobRequest.location.id : null,
            payerId: payerId,
            email: !this.userData.id ? this.userData.email : null,
            token: !this.userData.id ? this.eventTrackingService.persistentKey : null,
            prospectId: this.userData.prospectId,
            quoteStartDate: jobRequest.quoteStartDate ?? null,
        };

        const partHasQuantity = jobRequest.services
            .flatMap((s: Service) => s?.parts)
            .filter((p: Part) => p?.quantity > 0)
            .map((p: Part) => p?.quantity)?.length > 0;

        // send the entire services array if there are parts with quantities or ppm
        if (partHasQuantity || this.router.url.startsWith("/book/ppm")) {
            params.services = jobRequest.services.filter(UtilitiesService.isNotEmpty);
        } else {
            params.serviceCodes = jobRequest.services.filter(UtilitiesService.isNotEmpty).map((s: any) => s.serviceCode);
        }

        if (this.userData.id) {
            return this.jobService.getPrice(params, this.constructor.name).pipe(map(BookingFlowPricingService.mapGetPriceResponse));
        } else {
            return this.jobService.unauthenticatedEstimate(params).pipe(map(BookingFlowPricingService.mapGetPriceResponse));
        }
    } // getPrice()

    private static mapGetPriceResponse(response: any) {
        if (response.responseCode === 100
            && UtilitiesService.isNotEmpty(response.details)
            && UtilitiesService.isNotEmpty(response.details.transactions[0])
            && UtilitiesService.isNotEmpty(response.details.transactions[0].txnTotal)) {
            return response;
        } else {
            throw new Error(`${response.responseCode} : ${response.message}`);
        }
    } // mapGetPriceResponse()

    getUnauthedServicePrice(vehicleId: number, serviceCode: number, services?: any[]): Observable<any> {
        const params = {
            vehicleId: vehicleId,
            prospectId: this.userData.prospectId,
            zip: this.userData.zip,
            locationId: this.userData.locationId,
            services: services,
            serviceCodes: serviceCode ? [serviceCode] : [],
            token: this.eventTrackingService.persistentKey
        };
        return this.jobService.unauthenticatedEstimate(params);
    } // getUnauthedServicePrice()

    // used to get a price for a service (not service code)
    // example use case:
    //     Lemon Squad jobs must be priced via service, as the service object contains 'extraParams' for pricing
    getUnauthedServicePriceViaService(vehicleId: number, service: any, locationId: number): Observable<any> {
        const params: any = {
            vehicleId: vehicleId,
            prospectId: this.userData.prospectId,
            token: this.eventTrackingService.persistentKey,
            services: [service]
        };
        if (locationId) {
            params.locationId = locationId;
        } else {
            params.zip = this.userData.zip;
        }
        return this.jobService.unauthenticatedEstimate(params).pipe(
            map((response: any) => {
                return response;
            })
        );
    } // getUnauthedServicePriceViaService()
}
